'use client';

import styles from './currencySelector.module.scss';
import React, { useRef, useState } from 'react';
import CurrencyDropdown from './currencyDropdown/currencyDropdown';
import Text from '@JohnLewisPartnership/jlfs-ui-library/dist/components/inputs/text/text';
import getFlagUrl from '@JohnLewisPartnership/jlfs-ui-library/dist/utils/getFlagUrl';
import { MappedFresCurrency } from '../../../utils/getFresCurrencies/getFresCurrencies';

export type TCurrencySelector = {
  label?: string;
  currencyList?: Array<MappedFresCurrency>;
  selectedCurrency: MappedFresCurrency;
  inputValue?: string;
  disabled?: boolean;
  dropdownEnabled?: boolean;
  inputValueUpdated?: (value: string) => void;
  selectedCurrencyUpdated?: (currency: MappedFresCurrency) => void;
  focusUpdated?: (value: string) => void;
  error?: string;
  loadingSpinner?: React.ReactNode;
  monetateClickEvent?: string;
};

const moveCursorToEnd = (input: HTMLInputElement): void => {
  const endPosition = input.value.length;
  input.setSelectionRange(endPosition, endPosition);
};

export default function CurrencySelector({
  label,
  currencyList = [],
  selectedCurrency,
  inputValue,
  disabled = false,
  dropdownEnabled = true,
  inputValueUpdated,
  selectedCurrencyUpdated,
  focusUpdated,
  error,
  loadingSpinner,
  monetateClickEvent,
}: TCurrencySelector) {
  const [dropdownIsOpen, setDropdownIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const filteredCurrencyList = currencyList.filter(
    (currency) => currency.productId
  );

  const toggleDropdown = () => {
    setDropdownIsOpen(!dropdownIsOpen);
  };

  const handleKeyDown = (event: any): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDropdown();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setDropdownIsOpen(false);
    }
  };

  const handleOnChange = (event: any): void => {
    inputValueUpdated && inputValueUpdated(event?.target?.value);
  };

  const handleOnFocus = (event: any): void => {
    focusUpdated && focusUpdated(event?.target?.value);
    window.requestAnimationFrame(() => moveCursorToEnd(event.target));
  };

  const handleOverlayPointerDown = (event: React.PointerEvent): void => {
    event.preventDefault();
    inputRef.current?.focus();
    if (inputRef.current) moveCursorToEnd(inputRef.current);
  };

  const handleListItemClick = (event: any): void => {
    const index = event.currentTarget.getAttribute('data-item-index');
    const itemType = event.currentTarget.getAttribute('data-item-type');
    if (index && itemType) {
      const selectedCurrency = filteredCurrencyList[index];
      selectedCurrencyUpdated && selectedCurrencyUpdated(selectedCurrency);
      setDropdownIsOpen(false);
    }
  };

  const handleClickOutsideDropdown = (event: any) => {
    if (
      dropdownIsOpen &&
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target)
    ) {
      setDropdownIsOpen(false);
    }
  };

  return (
    <>
      <div className={styles.currencySelector}>
        <Text
          ref={inputRef}
          name="Currency Input"
          helpText={label ?? 'What you’ll get in your currency'}
          onChange={handleOnChange}
          value={inputValue}
          inputMode="decimal"
          disabled={disabled}
          error={error}
          onFocus={handleOnFocus}
          monetateClickEvent={monetateClickEvent}
        ></Text>
        <div
          className={styles.symbolClickOverlay}
          onPointerDown={handleOverlayPointerDown}
        ></div>
        <div
          {...(dropdownEnabled && {
            'aria-haspopup': true,
            'aria-expanded': dropdownIsOpen,
            role: 'button',
          })}
          tabIndex={dropdownEnabled ? 0 : -1}
          data-testid="selected-currency"
          onKeyDown={handleKeyDown}
          onMouseDown={toggleDropdown}
          className={styles.currencyIndicator}
        >
          {loadingSpinner}
          <span className={styles.currencyContainer}>
            <div className={styles.flagWrapper}>
              <img
                src={getFlagUrl(selectedCurrency?.countryCode ?? '')}
                alt={`Flag of ${selectedCurrency?.currencyName ?? selectedCurrency?.currencyCode ?? ''}`}
              ></img>
            </div>
            <span className={styles.currencyCode}>
              {selectedCurrency?.currencyCode ?? ''}
            </span>
          </span>
          <span
            className={`${styles.iconContainer}${dropdownEnabled ? ` ${styles.withDropdown}` : ''}`}
          >
            {dropdownEnabled && (
              <img
                data-testid="menu-indicator"
                src={
                  dropdownIsOpen
                    ? '/icons/chevron-up.svg'
                    : '/icons/chevron-down.svg'
                }
                alt=""
              ></img>
            )}
          </span>
        </div>
        {dropdownIsOpen && dropdownEnabled ? (
          <CurrencyDropdown
            setDropdownIsOpen={setDropdownIsOpen}
            currencyList={filteredCurrencyList}
            handleListItemClick={handleListItemClick}
            handleClick={handleClickOutsideDropdown}
            ref={dropdownRef}
          ></CurrencyDropdown>
        ) : null}
      </div>
    </>
  );
}
